import { logger } from '../../../../utils/logger';

/**
 * Monitors JsSIP UserAgent registration events for debugging.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function installJsSipUaEventListeners(audioCode: any, retryCount = 0): void {
  const MAX_RETRIES = 20;

  if (!audioCode) {
    logger.warn('[CXone SDK] Cannot install UA listeners: audioCode not available');
    return;
  }

  const jssipUA = audioCode.jssipUA;
  if (!jssipUA) {
    if (retryCount < MAX_RETRIES) {
      logger.debug(
        `[CXone SDK] jssipUA not available yet, retry ${retryCount + 1}/${MAX_RETRIES}...`
      );
      setTimeout(() => {
        installJsSipUaEventListeners(audioCode, retryCount + 1);
      }, 500);
    } else {
      logger.error('[CXone SDK] jssipUA never became available after 10 seconds');
    }
    return;
  }

  if (jssipUA.__uaListenersInstalled) {
    return;
  }

  logger.debug('[CXone SDK] Installing JsSIP UA event listeners for debugging...');
  logger.debug('[CXone SDK] JsSIP UA current state:', {
    isConnected: jssipUA.isConnected?.(),
    isRegistered: jssipUA.isRegistered?.(),
  });

  jssipUA.on('connecting', (data: { socket: unknown; attempts: number }) => {
    logger.debug('[CXone SDK] JsSIP UA: connecting', { attempts: data.attempts });
  });

  jssipUA.on('connected', () => {
    logger.debug('[CXone SDK] JsSIP UA: connected (WebSocket open)');
  });

  jssipUA.on(
    'disconnected',
    (data: { socket: unknown; error: boolean; code?: number; reason?: string }) => {
      logger.debug('[CXone SDK] JsSIP UA: disconnected', {
        error: data.error,
        code: data.code,
        reason: data.reason,
      });
    }
  );

  jssipUA.on('registered', () => {
    logger.debug('[CXone SDK] JsSIP UA: REGISTERED successfully!');
    logger.debug('[CXone SDK]   isRegistered():', jssipUA.isRegistered?.());
  });

  jssipUA.on('unregistered', (data: { response?: unknown; cause?: string }) => {
    logger.debug('[CXone SDK] JsSIP UA: unregistered', { cause: data.cause });
  });

  jssipUA.on('registrationFailed', (data: { response?: unknown; cause: string }) => {
    logger.error('[CXone SDK] JsSIP UA: REGISTRATION FAILED!');
    logger.error('[CXone SDK]   cause:', data.cause);
    if (data.response) {
      logger.error('[CXone SDK]   response:', data.response);
    }
  });

  jssipUA.on('registrationExpiring', () => {
    logger.debug('[CXone SDK] JsSIP UA: registration expiring, will re-register');
  });

  jssipUA.on(
    'newRTCSession',
    (data: { originator: string; session: unknown; request: unknown }) => {
      logger.debug('[CXone SDK] JsSIP UA: newRTCSession', {
        originator: data.originator,
      });
    }
  );

  jssipUA.on('newMessage', (data: { originator: string; message: unknown; request: unknown }) => {
    logger.debug('[CXone SDK] JsSIP UA: newMessage', { originator: data.originator });
  });

  jssipUA.__uaListenersInstalled = true;
  logger.debug('[CXone SDK] JsSIP UA event listeners installed');

  let checkCount = 0;
  const registrationCheckInterval = setInterval(() => {
    checkCount++;
    const isConnected = jssipUA.isConnected?.();
    const isRegistered = jssipUA.isRegistered?.();

    logger.debug(`[CXone SDK] Registration check #${checkCount}:`, {
      isConnected,
      isRegistered,
    });

    if (isRegistered || checkCount >= 30) {
      clearInterval(registrationCheckInterval);
      if (!isRegistered) {
        logger.warn('[CXone SDK] Registration did not complete within 30 seconds');
      }
    }
  }, 1000);
}
